import React, { useState, useEffect } from "react";
import axios from "axios";
import cookie from "react-cookies";
import { UNIFIED_BASE_URL } from "../../webbase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import KYCUpdateModal from "./kycUpdateModal";

const GetMerchantKYC = () => {
    const [merchantKYCs, setMerchantKYCs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [token] = useState(cookie.load("token"));
    const [filters, setFilters] = useState({
        email: "",
        businessName: "",
    });
    const [selectedEmail, setSelectedEmail] = useState("");

    useEffect(() => {
        fetchMerchantKYCs();
    }, []);

    const fetchMerchantKYCs = async () => {
        try {
            const response = await axios.get(`${UNIFIED_BASE_URL}/onboarding/merchants-all-kyc`, {
                headers: {
                    Authorization: token,
                },
            });
            if (response.data.status === "2000") {
                setMerchantKYCs(response.data.data);
            } else {
                toast.error(response.data.description || "Failed to fetch merchant KYC details");
            }
        } catch (error) {
            console.error("Error fetching merchant KYCs:", error);
            toast.error("Error fetching merchant KYC details");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getStatusBadgeClass = (status) => {
        switch (status.toLowerCase()) {
            case "approved":
                return "bg-success";
            case "rejected":
                return "bg-danger";
            case "pending":
                return "bg-warning";
            default:
                return "bg-secondary";
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateClick = (email) => {
        setSelectedEmail(email);
    };

    const filteredMerchantKYCs = merchantKYCs.filter(kyc => {
        const emailMatch = kyc.emailAddress.toLowerCase().includes(filters.email.toLowerCase());
        const businessNameMatch = kyc.businessName.toLowerCase().includes(filters.businessName.toLowerCase());
        return emailMatch && businessNameMatch;
    });

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <ToastContainer />
            <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
                <div className="ps-3">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-0 p-0">
                            <li className="breadcrumb-item">
                                <a href="javascript:;">
                                    <i className="bx bx-home-alt"></i>
                                </a>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                Merchant KYC Details
                            </li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                        <h5 className="mb-0">Merchant KYC Details</h5>
                        <div className="ms-auto">
                            <button 
                                className="btn btn-primary btn-sm"
                                onClick={fetchMerchantKYCs}
                            >
                                <i className="bi bi-arrow-clockwise"></i> Refresh
                            </button>
                        </div>
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="bi bi-envelope"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Filter by email"
                                    name="email"
                                    value={filters.email}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="bi bi-building"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Filter by business name"
                                    name="businessName"
                                    value={filters.businessName}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>ID</th>
                                    <th>Business Name</th>
                                    <th>Email</th>
                                    <th>NIN</th>
                                    <th>Status</th>
                                    <th>Date Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMerchantKYCs.map((kyc) => (
                                    <tr key={kyc.id}>
                                        <td>{kyc.id}</td>
                                        <td>{kyc.businessName}</td>
                                        <td>{kyc.emailAddress}</td>
                                        <td>{kyc.nin}</td>
                                        <td>
                                            <span className={`badge ${getStatusBadgeClass(kyc.status)}`}>
                                                {kyc.status}
                                            </span>
                                        </td>
                                        <td>{formatDate(kyc.dateCreated)}</td>
                                        <td>
                                            <div className="table-actions d-flex align-items-center gap-3 fs-6">
                                                {kyc.imageUrl && (
                                                    <a
                                                        href={kyc.imageUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-warning"
                                                        title="View KYC Document"
                                                    >
                                                        <i className="bi bi-file-earmark-text"></i>
                                                    </a>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleUpdateClick(kyc.emailAddress)}
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#kycUpdateModal"
                                                >
                                                    Update Status
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredMerchantKYCs.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="text-center">
                                            No merchant KYC details found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <KYCUpdateModal
                emailAddress={selectedEmail}
                onUpdate={fetchMerchantKYCs}
            />
        </div>
    );
};

export default GetMerchantKYC;
